import React from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
// TODO: Rename this css file and use for others
import './bloodwork-anemia-status.css';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowUpStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px'
};

const arrowDownStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px'
};

const conditionTitleStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'left'
};

const conditionListStyle: React.CSSProperties = {
  paddingLeft: '12px',
  marginLeft: 0,
  listStylePosition: 'inside'
};

const markerStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'right'
};

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export class WBCEvaluation extends React.Component<Props, {}> {

  renderArrow(direction, marker) {
    return (
      <>
        <img
          src={direction === 'up' ? arrow_up : arrow_down}
          alt={direction === 'up' ? '↑' : '↓'}
          style={direction === 'up' ? arrowUpStyle : arrowDownStyle}
        />
        {marker}
      </>
    );
  }

  markerIsHigh = (marker) => {
    return marker?.isGreaterThanLab?.() || marker?.isGreaterThanOptimal?.();
  };

  markerIsLow = (marker) => {
    return marker?.isLessThanLab?.() || marker?.isLessThanOptimal?.();
  };

  markerIsOptimal = (marker) => {
    return marker?.hasValue?.() && !this.markerIsHigh(marker) && !this.markerIsLow(marker);
  };

  renderPatternBox = (title, bullets, markers) => {
    return (
      <div className="condition-section">
        <div style={conditionTitleStyle}>
          <div>{title}</div>
          <div>Consistent with:</div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <ul style={conditionListStyle}>
            {bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
        <div style={markerStyle}>
          {markers.map((marker, index) => (
            <React.Fragment key={`${marker.direction}-${marker.label}-${index}`}>
              {index > 0 && <>,{'\u00A0'}</>}
              {this.renderArrow(marker.direction, marker.label)}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // 70001 and 70002
  renderImmuneActivityStatus = (bloodworkAnalysis) => {
    const wbc = bloodworkAnalysis?.wbc;
    if (!wbc?.hasValue?.()) return null;

    if (this.markerIsHigh(wbc)) {
      return this.renderPatternBox(
        'Acute Immune Activity',
        [
          'Recent Infection',
          'Acute Immune Trigger',
          'Acute Inflammation',
          'Tissue Injury'
        ],
        [{ direction: 'up', label: 'WBC' }]
      );
    }

    if (this.markerIsLow(wbc)) {
      return this.renderPatternBox(
        'Suppressed Immune Activity',
        [
          'Chronic Stress / Elevated Cortisol',
          'Nutrient Insufficiency',
          'Viral-type Activity',
          'Unresolved Immune Trigger'
        ],
        [{ direction: 'down', label: 'WBC' }]
      );
    }

    return null;
  };

  // 70008 and 70009
  renderImmuneResponseBalance = (bloodworkAnalysis) => {
    const wbc = bloodworkAnalysis?.wbc;
    const nlr = bloodworkAnalysis?.nlr;

    if (!nlr?.hasValue?.()) return null;

    // These NLR-only patterns should only show when WBC is optimal.
    // If WBC is high or low, the combined WBC + NLR patterns below take priority.
    if (!this.markerIsOptimal(wbc)) return null;

    if (this.markerIsHigh(nlr)) {
      return this.renderPatternBox(
        'Innate Immune Activity',
        [
          'Bacterial-type Activity',
          'Acute Inflammatory Activity',
          'Acute Stress',
          'Tissue Injury'
        ],
        [{ direction: 'up', label: 'NLR' }]
      );
    }

    if (this.markerIsLow(nlr)) {
      return this.renderPatternBox(
        'Adaptive Immune Activity',
        [
          'Viral-type Activity',
          'Autoimmune-type Activity',
          'Chronic Stress',
          'Unresolved Immune Trigger'
        ],
        [{ direction: 'down', label: 'NLR' }]
      );
    }

    return null;
  };

  // 70004
  renderAcuteInnateImmuneResponseBox = (analysis) => {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcHigh = this.markerIsHigh(wbc);
    const nlrHigh = this.markerIsHigh(nlr);

    if (wbcHigh && nlrHigh) {
      return this.renderPatternBox(
        'Acute Innate Immune Activity',
        [
          'Bacterial-type Activity',
          'Acute Inflammatory Activity',
          'Acute Stress',
          'Tissue Injury'
        ],
        [
          { direction: 'up', label: 'WBC' },
          { direction: 'up', label: 'NLR' }
        ]
      );
    }

    return null;
  };

  // 70005
  renderAcuteAdaptiveImmuneResponse = (analysis) => {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcHigh = this.markerIsHigh(wbc);
    const nlrLow = this.markerIsLow(nlr);

    if (wbcHigh && nlrLow) {
      return this.renderPatternBox(
        'Acute Adaptive Immune Activity',
        [
          'Viral-type Activity',
          'Autoimmune-type Activity',
          'Acute Stress',
          'Unresolved Immune Trigger'
        ],
        [
          { direction: 'up', label: 'WBC' },
          { direction: 'down', label: 'NLR' }
        ]
      );
    }

    return null;
  };

  // 70006
  renderChronicInnateImmuneResponse = (analysis) => {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcLow = this.markerIsLow(wbc);
    const nlrHigh = this.markerIsHigh(nlr);

    if (wbcLow && nlrHigh) {
      return this.renderPatternBox(
        'Chronic Innate Immune Activity',
        [
          'Bacterial-type Activity',
          'Chronic Inflammatory Activity',
          'Chronic Stress',
          'Unresolved Immune Trigger'
        ],
        [
          { direction: 'down', label: 'WBC' },
          { direction: 'up', label: 'NLR' }
        ]
      );
    }

    return null;
  };

  // 70007
  renderChronicAdaptiveImmuneResponse = (analysis) => {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcLow = this.markerIsLow(wbc);
    const nlrLow = this.markerIsLow(nlr);

    if (wbcLow && nlrLow) {
      return this.renderPatternBox(
        'Chronic Adaptive Immune Activity',
        [
          'Viral-type Activity',
          'Autoimmune-type Activity',
          'Chronic Stress',
          'Unresolved Immune Trigger'
        ],
        [
          { direction: 'down', label: 'WBC' },
          { direction: 'down', label: 'NLR' }
        ]
      );
    }

    return null;
  };

  isEosinophilsHigh = (analysis) => {
    const eosAbs = analysis?.eosinophilsAbs;
    const eosPct = analysis?.eosinophils;
    return this.markerIsHigh(eosAbs) || this.markerIsHigh(eosPct);
  };

  isMonocytesHigh = (analysis) => {
    const monoAbs = analysis?.monocytesAbs;
    const monoPct = analysis?.monocytes;
    return this.markerIsHigh(monoAbs) || this.markerIsHigh(monoPct);
  };

  isBasophilsHigh = (analysis) => {
    const basoAbs = analysis?.basophilsAbs;
    const basoPct = analysis?.basophils;
    return this.markerIsHigh(basoAbs) || this.markerIsHigh(basoPct);
  };

  // 70010
  renderBroadImmuneActivity = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (eosHigh && monoHigh && basoHigh) {
      return this.renderPatternBox(
        'Broad Immune Activity',
        [
          'Allergic-type Activity',
          'Parasitic-type Activity',
          'Histamine Release',
          'Cleanup and Repair Activity'
        ],
        [
          { direction: 'up', label: 'Eos' },
          { direction: 'up', label: 'Mono' },
          { direction: 'up', label: 'Baso' }
        ]
      );
    }

    return null;
  };

  // 70011
  renderAllergicImmuneActivityWithCleanupRepair = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (eosHigh && monoHigh && !basoHigh) {
      return this.renderPatternBox(
        'Allergic-Type Immune Activity with Cleanup and Repair',
        [
          'Allergic-type Activity',
          'Food-related Triggers',
          'Environmental Triggers',
          'Parasitic-type Activity'
        ],
        [
          { direction: 'up', label: 'Eos' },
          { direction: 'up', label: 'Mono' }
        ]
      );
    }

    return null;
  };

  // 70012
  renderAllergicHistamineImmuneActivity = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (eosHigh && basoHigh && !monoHigh) {
      return this.renderPatternBox(
        'Allergic-Type and Histamine-Related Immune Activity',
        [
          'Allergic-type Activity',
          'Food-related Triggers',
          'Environmental Triggers',
          'Histamine Release'
        ],
        [
          { direction: 'up', label: 'Eos' },
          { direction: 'up', label: 'Baso' }
        ]
      );
    }

    return null;
  };

  // 70013
  renderMonocyteEvaluation = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (monoHigh && !eosHigh && !basoHigh) {
      return this.renderPatternBox(
        'Immune Cleanup and Repair Activity',
        [
          'Recent Immune Activity',
          'Tissue Recovery',
          'Resolving Inflammation'
        ],
        [{ direction: 'up', label: 'Mono' }]
      );
    }

    return null;
  };

  // 70014
  renderEosinophilsAlone = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (eosHigh && !monoHigh && !basoHigh) {
      return this.renderPatternBox(
        'Allergic-Type Immune Activity',
        [
          'Allergic-type Activity',
          'Food-related Triggers',
          'Environmental Triggers',
          'Parasitic-type Activity'
        ],
        [{ direction: 'up', label: 'Eos' }]
      );
    }

    return null;
  };

  // 70015
  renderBasoMonoPattern = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (monoHigh && basoHigh && !eosHigh) {
      return this.renderPatternBox(
        'Histamine-Related Immune Activity with Cleanup and Repair',
        [
          'Histamine Release',
          'Cleanup and Repair Activity',
          'Tissue Recovery',
          'Resolving Inflammation'
        ],
        [
          { direction: 'up', label: 'Mono' },
          { direction: 'up', label: 'Baso' }
        ]
      );
    }

    return null;
  };

  // 70016
  renderBasophilEvaluation = (analysis) => {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (basoHigh && !eosHigh && !monoHigh) {
      return this.renderPatternBox(
        'Histamine-Related Immune Activity',
        [
          'Histamine Release',
          'Mild Immune Triggers',
          'Early Immune Activity'
        ],
        [{ direction: 'up', label: 'Baso' }]
      );
    }

    return null;
  };

  openWindow = () => {
    window.open('https://labsmarts.com/testresultsanalysis/#wbc');
  }

  render() {
    const { bloodworkAnalysis } = this.props;

    if (!bloodworkAnalysis.bloodwork) return null;

    const wbc = bloodworkAnalysis?.wbc;

    // Hide panel entirely if WBC has no value
    if (!wbc?.hasValue?.()) return null;

    const showAcuteInnate = this.renderAcuteInnateImmuneResponseBox(bloodworkAnalysis);
    const showAcuteAdaptive = this.renderAcuteAdaptiveImmuneResponse(bloodworkAnalysis);
    const showChronicInnate = this.renderChronicInnateImmuneResponse(bloodworkAnalysis);
    const showChronicAdaptive = this.renderChronicAdaptiveImmuneResponse(bloodworkAnalysis);

    const showImmuneActivity = this.renderImmuneActivityStatus(bloodworkAnalysis);
    const showImmuneBalance = this.renderImmuneResponseBalance(bloodworkAnalysis);

    // Only show the single-marker WBC or NLR patterns when none of the combined WBC + NLR patterns triggered.
    const showNoMajorPatterns = !showAcuteInnate && !showAcuteAdaptive && !showChronicInnate && !showChronicAdaptive;

    const panelTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about the items in this panel.
      </Tooltip>
    );

    return (
      <Card className="evaluationPanel" id="wbc-evaluation">
        <Card.Header className="evaluationPanelHeading">
          Immune Activity Insights&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon icon="external-link-alt" onClick={this.openWindow} />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text="Immune System Abnormality is"
              chartContainerId="anemiaStatusGuage"
              bloodwork={bloodworkAnalysis.bloodwork}
              category={bloodworkAnalysis.wbcAnalysis.immuneSystemDisorderProbabilityCategory}
              probability={bloodworkAnalysis.wbcAnalysis.immuneSystemDisorderProbability}
            />

            {showAcuteInnate}
            {showAcuteAdaptive}
            {showChronicInnate}
            {showChronicAdaptive}

            {showNoMajorPatterns && showImmuneActivity}
            {showNoMajorPatterns && showImmuneBalance}

            {this.renderBroadImmuneActivity(bloodworkAnalysis)}
            {this.renderAllergicImmuneActivityWithCleanupRepair(bloodworkAnalysis)}
            {this.renderAllergicHistamineImmuneActivity(bloodworkAnalysis)}
            {this.renderMonocyteEvaluation(bloodworkAnalysis)}
            {this.renderEosinophilsAlone(bloodworkAnalysis)}
            {this.renderBasoMonoPattern(bloodworkAnalysis)}
            {this.renderBasophilEvaluation(bloodworkAnalysis)}
          </div>
        </Card.Body>
      </Card>
    );
  }
};
